<template>
  <div class="event-header">
    <div class="event-header__background">
      <event-image
        class="activity-preview__icon"
        :eventId="this.eventId"
        :alt="eventData.name"
      />
    </div>

    <div class="event-header__heading">
      <h1 class="event-header__title" v-if="eventData.name">
        {{ eventData.name }}
      </h1>

      <div class="event-header__privacy">
        <el-tag type="primary">
          {{ eventData.isPrivate ? 'private' : 'public' }}
        </el-tag>
      </div>

      <div v-if="eventData.demo" class="event-header__demo">
        <!--span class="event-header__separator">&middot;</span-->
        <el-tag type="primary">
          Demo
        </el-tag>
      </div>

      <div class="event-header__buttons">
        <el-button
          type="primary"
          @click="joinEvent"
          v-if="eventData.canSendApplication"
        >
          Join
        </el-button>
        <el-button
          type="primary"
          v-if="!eventData.hasAdminRights"
          @click="isContactAdminFormVisible = true"
          :disabled="isButtonDisabled('contact-admin')"
        >
          Contact organizer
        </el-button>

        <el-button
          type="primary"
          v-if="
            eventData.status !== 'Canceled' &&
              eventData.status !== 'Draft' &&
              eventData.hasAdminRights
          "
          @click="toSearchHeroes"
        >
          Invite participants
        </el-button>
        <el-button
          type="primary"
          @click="handleCancelEvent"
          v-if="eventData.status !== 'Canceled' && eventData.hasAdminRights"
          :disabled="isButtonDisabled('change-event-status')"
        >
          Cancel
        </el-button>
        <el-button
          type="primary"
          @click="handleDeleteEvent"
          v-if="eventData.hasAdminRights"
          :disabled="isButtonDisabled('change-event-status')"
        >
          Delete
        </el-button>
        <el-button
          type="primary"
          @click="handleActivateEvent"
          v-if="
            (eventData.status === 'Canceled' || eventData.status === 'Draft') &&
              eventData.hasAdminRights
          "
          :disabled="isButtonDisabled('change-event-status')"
        >
          Activate
        </el-button>
        <el-button
          type="primary"
          @click="editEvent"
          v-if="eventData.hasAdminRights"
        >
          Edit
        </el-button>
        <el-button type="primary" @click="handleShare" disabled>
          Share
        </el-button>
      </div>
    </div>

    <div
      :class="[
        'event-header__status',
        {
          'event-header__status--success': eventData.status === 'Finished',
          'event-header__status--negative': eventData.status === 'Canceled',
        },
      ]"
      v-if="eventData.status"
    >
      {{ eventData.status }}
    </div>

    <div
      class="event-header__type"
      v-if="eventData.activityName && eventData.eventType"
    >
      <span class="event-header__type--icon">
        <activity-icon
          class="activity-preview__icon"
          :size="32"
          :activity="activityId"
        />
        {{ eventData.activityName }}
      </span>
      <span>
        {{ eventData.eventType }}
      </span>
    </div>

    <div class="event-header__level" v-if="eventData.eventLevelType">
      Level: {{ eventData.eventLevelType.name }}
    </div>

    <div class="event-header__content">
      <div class="event-header__info">
        <div
          class="event-header__info-item"
          v-if="eventData.startDate || eventData.endDate"
        >
          <i class="el-icon-time" />
          <span v-if="eventData.startDate">
            {{ formatDate(eventData.startDate, SHORT_DATETIME_FORMAT) }}
          </span>
          <span v-if="eventData.startDate && eventData.endDate">
            — {{ formatDate(eventData.endDate, SHORT_DATETIME_FORMAT) }}
          </span>
        </div>
        <div class="event-header__info-item" v-if="eventData.location">
          <i class="el-icon-location-outline" />
          <a
            :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventData.location)}`"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ eventData.location }}
          </a>
        </div>
        <div class="event-header__info-item" v-if="eventData.participantCount">
          <i class="el-icon-user" />
          {{ eventData.participantCount }}
          {{
            eventData.participantCount === 1 ? 'participant' : 'participants'
          }}
        </div>
      </div>

      <div
        class="event-header__socials"
        v-if="eventData.eventSocialNetworks.length > 0"
      >
        <a
          class="event-header__social"
          v-for="(social, index) in eventData.eventSocialNetworks"
          :key="index"
          :href="social.socialNetworkBaseUrl + social.accountLink"
          target="_blank"
        >
          <div class="event-header__social-name">{{ social.name }}:</div>
          <span class="event-header__social-address">
            {{ social.accountLink }}
          </span>
        </a>
      </div>
    </div>

    <div class="event-header__description" v-if="eventData.details">
      {{ eventData.details }}
    </div>

    <el-dialog
      v-model="isConfirmationPopupVisible"
      width="30%"
      title="Confirmation"
      v-if="eventData.hasAdminRights"
    >
      <span>
        {{ confirmationMessage }}
      </span>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleCloseConfirmationPopup">
            Cancel
          </el-button>
          <el-button
            type="primary"
            @click="handleConfirmation"
            :disabled="isButtonDisabled('change-event-status')"
          >
            {{ confirmationButtonLabel }}
          </el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog
      v-model="isContactAdminFormVisible"
      width="40%"
      title="Contact organizer"
      v-if="!eventData.hasAdminRights"
    >
      <el-form
        :rules="contactAdminRules"
        :model="contactAdminForm"
        ref="contactAdminForm"
      >
        <el-form-item
          prop="text"
          :error="contactAdminErrors.text"
          label="Message"
        >
          <el-input v-model="contactAdminForm.text" type="textarea" :rows="4">
          </el-input>
        </el-form-item>
      </el-form>

      <template #footer>
      <span class="dialog-footer">
        <el-button @click="isContactAdminFormVisible = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          @click="validateContactAdminForm"
          :disabled="isButtonDisabled('contact-admin')"
        >
          Send message
        </el-button>
      </span>
    </template>
    </el-dialog>

    <join-event
      :event-data="eventData"
      :event-id="eventId"
      v-if="isJoinEventFormVisible"
      @on-cancel="handleCancelJoinEvent"
    />

    <div class="event-header__buttons">
      <el-dropdown>
        <el-button type="primary">
          Add to calendar <i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <template v-slot:dropdown>
          <el-dropdown-menu>
            <el-dropdown-item @click="addToGoogleCalendar">Google Calendar</el-dropdown-item>
            <el-dropdown-item @click="addToOutlook">Outlook</el-dropdown-item>
            <el-dropdown-item @click="addToICal">iCal</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/common/Icon.vue';
import JoinEvent from '@/components/events/JoinEvent.vue';
import ActivityIcon from '@/components/common/ActivityIcon.vue';
import EventImage from '@/components/events/EventImage.vue';
import {
  formatDate,
  SHORT_DATETIME_FORMAT,
  parseErrorsByFields,
} from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import {
  cancelEvent,
  deleteEvent,
  publishEvent,
  sendMessageToEventAdmin,
} from '@/api/events.js';
import { disableButton } from '@/mixins/disableButton.js';

export default {
  name: 'EventHeader',
  props: {
    eventData: {
      type: Object,
      required: true,
    },
    eventId: {
      type: Number,
      required: true,
    },
  },
  mixins: [disableButton],
  components: {
    Icon,
    JoinEvent,
    ActivityIcon,
    EventImage,
  },
  data() {
    return {
      SHORT_DATETIME_FORMAT,
      isConfirmationPopupVisible: false,
      confirmationMessage: '',
      confirmationButtonLabel: '',

      isContactAdminFormVisible: false,
      contactAdminForm: { text: null },
      contactAdminRules: {
        text: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter your message',
          },
        ],
      },
      contactAdminErrors: { text: null },
      isJoinEventFormVisible: false,
    };
  },
  computed: {
    descriptions() {
      return this.$store.state.descriptions;
    },
    activityId() {
      const { activityType } = this.descriptions;
      const { activityName } = this.eventData;

      return activityType.find(
        (activity) => activity.description === activityName,
      ).value;
    },
  },
  methods: {
    formatDate,
    showMessage,
    parseErrorsByFields,
    async requestCancelEvent() {
      const { eventId } = this;
      this.disableButton('change-event-status');

      const { success } = await cancelEvent({ eventId });
      this.enableButton('change-event-status');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Event has been canceled',
        });
        this.$emit('update-data');
        this.isConfirmationPopupVisible = false;
        this.confirmationMessage = '';
        this.confirmationButtonLabel = '';
      }
    },
    async requestDeleteEvent() {
      const { eventId } = this;
      this.disableButton('change-event-status');

      const { success } = await deleteEvent({ eventId });
      this.enableButton('change-event-status');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Event has been deleted',
        });

        this.$router.push({
          name: 'Events',
        });
      }
    },
    async requestPublishEvent() {
      const { eventId } = this;
      this.disableButton('change-event-status');

      const { success } = await publishEvent({ eventId });
      this.enableButton('change-event-status');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Event has been activated',
        });
        this.$emit('update-data');
        this.isConfirmationPopupVisible = false;
        this.confirmationMessage = '';
        this.confirmationButtonLabel = '';
      }
    },
    async requestContactAdmin() {
      const { eventId } = this;
      const { text } = this.contactAdminForm;
      this.disableButton('contact-admin');

      const { success, errors } = await sendMessageToEventAdmin({
        eventId,
        text,
      });
      this.enableButton('contact-admin');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Your message has been sent',
        });

        this.isContactAdminFormVisible = false;
        this.contactAdminForm.text = null;
        this.contactAdminErrors.text = null;
      } else if (errors) {
        this.contactAdminErrors = {
          ...this.contactAdminErrors,
          ...this.parseErrorsByFields(errors),
        };
      }
    },
    handleShare() {
      console.log('share');
    },
    editEvent() {
      const { eventId } = this;

      this.$router.push({
        name: 'EventForm',
        query: {
          edit: true,
          eventId,
        },
      });
    },
    convertToLowerCase(data) {
      return data.toLowerCase();
    },
    handleCloseConfirmationPopup() {
      this.isConfirmationPopupVisible = false;
      this.confirmationMessage = '';
      this.confirmationButtonLabel = '';
    },
    handleConfirmation() {
      const { confirmationButtonLabel } = this;
      if (confirmationButtonLabel === 'Activate') {
        this.requestPublishEvent();
      } else if (confirmationButtonLabel === 'Cancel event') {
        this.requestCancelEvent();
      } else if (confirmationButtonLabel === 'Delete') {
        this.requestDeleteEvent();
      }
    },
    handleCancelEvent() {
      this.isConfirmationPopupVisible = true;
      this.confirmationMessage =
        'Are you sure you want to cancel event? You can activate event later.';
      this.confirmationButtonLabel = 'Cancel event';
    },
    handleDeleteEvent() {
      this.isConfirmationPopupVisible = true;
      this.confirmationMessage =
        'Are you sure you want to delete event? You will not be able to activate the event later.';
      this.confirmationButtonLabel = 'Delete';
    },
    handleActivateEvent() {
      this.isConfirmationPopupVisible = true;
      this.confirmationMessage =
        this.eventData.status === 'Draft'
          ? 'Are you sure you want to activate event? It will be visible to all users even if you cancel it.'
          : 'Are you sure you want to activate event?';
      this.confirmationButtonLabel = 'Activate';
    },
    validateContactAdminForm() {
      const { text } = this.contactAdminForm;

      if (this.$refs.contactAdminForm) {
        this.$refs.contactAdminForm.validate();
      }

      if (text && text.trim()) {
        this.requestContactAdmin();
      }
    },
    toSearchHeroes() {
      const { eventId } = this;
      this.$router.push({
        name: 'SearchHeroes',
        query: {
          eventId,
        },
      });
    },
    joinEvent() {
      this.isJoinEventFormVisible = true;
    },
    handleCancelJoinEvent() {
      this.isJoinEventFormVisible = false;
    },
    formatICSDate(date) {
      return new Date(date).toISOString().replace(/[-:]|\.\d{3}/g, '');
    },

    addToGoogleCalendar() {
      const start = this.formatICSDate(this.eventData.startDate);
      const end = this.formatICSDate(this.eventData.endDate);
      const title = encodeURIComponent(this.eventData.name);
      const details = encodeURIComponent(this.eventData.details || '');
      const location = encodeURIComponent(this.eventData.location || '');

      const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
      window.open(googleCalendarUrl, '_blank');
    },

    addToOutlook() {
      const start = this.formatICSDate(this.eventData.startDate);
      const end = this.formatICSDate(this.eventData.endDate);
      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:${this.eventData.name}
DTSTART:${start}
DTEND:${end}
DESCRIPTION:${this.eventData.details || ''}
LOCATION:${this.eventData.location || ''}
END:VEVENT
END:VCALENDAR`;

      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'event.ics';
      link.click();
      URL.revokeObjectURL(url);
    },

    addToICal() {
      this.addToOutlook(); // iCal also uses .ics files, so we reuse the Outlook logic.
    },
  },
};
</script>

<style lang="scss">
@import '~@/styles/shared/_globals.scss';

.event-header {
  overflow: hidden;
  padding: 0 var(--global-dimension-size-300) var(--global-dimension-size-400);
  background-color: var(--color-gray-50);
  border-radius: var(--border-radius-base);
  margin-bottom: 30px;
  line-height: 1.6;

  &__background {
    position: relative;
    min-width: 100%;
    height: 200px;
    margin-left: -24px; // --global-dimension-size-300
    margin-right: -24px;

    img {
      position: absolute;
      width: 100%;
      height: 100%;

      object-fit: cover;
      object-position: center;
    }
  }

  &__heading {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    margin-top: var(--global-dimension-size-125);

    @include from('xl') {
      flex-wrap: nowrap;
      justify-content: space-between;
    }
  }

  &__title {
    margin: 0;
    margin-right: var(--global-dimension-size-200);
    margin-bottom: var(--global-dimension-size-100);

    font-weight: 500;
    font-size: var(--font-size-800);
    line-height: 1.6;
  }

  &__status {
    margin-bottom: var(--global-dimension-size-200);

    font-size: var(--font-size-200);
    font-weight: var(--global-font-weight-bold);
    color: var(--color-primary-400);

    &--success {
      color: var(--color-success-400);
    }
    &--negative {
      color: var(--color-danger-400);
    }
  }

  &__privacy {
    margin-right: var(--global-dimension-size-200);
    margin-bottom: var(--global-dimension-size-200);
  }

  &__demo {
    margin-right: var(--global-dimension-size-200);
    margin-bottom: var(--global-dimension-size-200);
  }

  &__buttons {
    .el-button {
      margin-bottom: var(--global-dimension-size-125);
    }

    @include from('xl') {
      flex-grow: 1;
      text-align: right;
    }
  }

  &__type {
    display: flex;
    align-items: center;
    font-weight: normal;
    font-size: var(--font-size-200);
    line-height: 1.6;
    margin-bottom: var(--global-dimension-size-225);

    span + span {
      position: relative;
      margin-left: 25px;

      &:before {
        position: absolute;
        content: '';
        left: -15px;
        top: 50%;

        transform: translateY(-50%);

        width: 5px;
        height: 5px;
        background-color: var(--color-gray-800);
        border-radius: 50%;
      }
    }

    &--icon {
      display: flex;
      align-items: center;
    }
  }

  &__level {
    margin-bottom: var(--global-dimension-size-225);
    font-size: var(--font-size-200);
  }

  &__content {
    margin-bottom: var(--global-dimension-size-75);
    display: flex;
    flex-flow: row wrap;
    font-size: var(--font-size-200);

    @include from('lg') {
      flex-wrap: nowrap;
    }
  }

  &__info {
    margin-right: var(--global-dimension-size-400);
  }

  &__info-item {
    margin-bottom: var(--global-dimension-size-175);
  }

  &__socials {
  }

  &__social {
    display: flex;
    align-items: center;
    margin-bottom: var(--global-dimension-size-175);
    color: var(--color-gray-900);

    &:hover {
      color: var(--color-primary-400);
    }
  }

  &__social-name {
    font-weight: var(--global-font-weight-bold);
    margin-right: var(--global-dimension-size-75);
  }

  &__description {
    font-size: var(--font-size-200);
  }
}
</style>
